/**
 * `defer-pickers-oauth-flow` — request/response shapes for the
 * "Connect Gmail / Outlook" status pills + connect dance that
 * sit beside the email-OTP preset menu.
 *
 * The actual OAuth token exchange happens in the API
 * `integrations` service; the editor only sees the metadata it
 * needs to render a status pill + drive the start / callback /
 * disconnect flow.
 */

import { z } from 'zod';

export const IntegrationProviderSchema = z.enum(['gmail', 'outlook', 'imap']);
export type IntegrationProvider = z.infer<typeof IntegrationProviderSchema>;

export const IntegrationConnectionStatusSchema = z.enum(['pending', 'connected', 'error', 'revoked']);
export type IntegrationConnectionStatus = z.infer<typeof IntegrationConnectionStatusSchema>;

/**
 * Read-side projection used by the editor + admin UIs. Tokens
 * themselves NEVER cross this wire — operators see `vaultKey` so
 * they can rotate the secret in vault directly if they want.
 */
export const IntegrationConnectionRowSchema = z.object({
  id: z.string().uuid(),
  provider: IntegrationProviderSchema,
  vaultKey: z.string().min(1),
  accountLabel: z.string().nullable(),
  scopes: z.array(z.string()),
  status: IntegrationConnectionStatusSchema,
  lastError: z.string().nullable(),
  expiresAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type IntegrationConnectionRow = z.infer<typeof IntegrationConnectionRowSchema>;

/** GET /api/integrations/connections */
export const ListIntegrationConnectionsResponseSchema = z.object({
  connections: z.array(IntegrationConnectionRowSchema),
});
export type ListIntegrationConnectionsResponse = z.infer<typeof ListIntegrationConnectionsResponseSchema>;

/** POST /api/integrations/:provider/start */
export const StartIntegrationConnectionInputSchema = z.object({
  /**
   * Operator-supplied label so multiple QA accounts under the same
   * provider can co-exist (e.g. `qa-eu@acme.com`, `qa-us@acme.com`).
   * The editor pre-fills this from the email-preset `mailboxId`.
   */
  accountLabel: z.string().min(1).max(160).optional().nullable(),
  /** Returns the user to this editor route after the OAuth dance. */
  returnUrl: z.string().min(1).max(2048).optional().nullable(),
});
export type StartIntegrationConnectionInput = z.infer<typeof StartIntegrationConnectionInputSchema>;

export const StartIntegrationConnectionResponseSchema = z.object({
  ok: z.literal(true),
  /** Provider's OAuth authorisation URL — the editor opens this in a popup. */
  authUrl: z.string().min(1),
  /** Opaque state token; the editor echoes it back to the complete endpoint. */
  state: z.string().min(1),
  /** Pre-allocated connection id so the pill can show a "pending" state immediately. */
  connectionId: z.string().uuid(),
});
export type StartIntegrationConnectionResponse = z.infer<typeof StartIntegrationConnectionResponseSchema>;

/** POST /api/integrations/:provider/complete */
export const CompleteIntegrationConnectionInputSchema = z.object({
  code: z.string().min(1),
  state: z.string().min(1),
});
export type CompleteIntegrationConnectionInput = z.infer<typeof CompleteIntegrationConnectionInputSchema>;

export const CompleteIntegrationConnectionResponseSchema = z.object({
  ok: z.literal(true),
  connection: IntegrationConnectionRowSchema,
});
export type CompleteIntegrationConnectionResponse = z.infer<typeof CompleteIntegrationConnectionResponseSchema>;

/** DELETE /api/integrations/connections/:id */
export const DisconnectIntegrationResponseSchema = z.object({
  ok: z.literal(true),
  removed: z.number().int().min(0).max(1),
});
export type DisconnectIntegrationResponse = z.infer<typeof DisconnectIntegrationResponseSchema>;

// ----------------------------------------------------------------------------
// Pure helpers — used by the editor + admin UIs to render status
// pills without round-tripping. Kept in `contracts` so the runner +
// other consumers can share the lookup.
// ----------------------------------------------------------------------------

/** Static metadata about each OAuth-style provider. */
export interface IntegrationProviderInfo {
  id: IntegrationProvider;
  label: string;
  /** Required OAuth scopes. */
  scopes: string[];
  /** Provider docs URL — used by the connect modal. */
  docsUrl: string;
  /** Authorise endpoint base. */
  authEndpoint: string;
  /** Token-exchange endpoint base. */
  tokenEndpoint: string;
  /** Stable, well-known issuer (so the editor can validate ids). */
  issuer: string;
}

export const INTEGRATION_PROVIDERS: Record<IntegrationProvider, IntegrationProviderInfo> = {
  gmail: {
    id: 'gmail',
    label: 'Gmail',
    scopes: ['https://www.googleapis.com/auth/gmail.readonly'],
    docsUrl: 'https://developers.google.com/gmail/api',
    authEndpoint: 'https://accounts.google.com/o/oauth2/v2/auth',
    tokenEndpoint: 'https://oauth2.googleapis.com/token',
    issuer: 'accounts.google.com',
  },
  outlook: {
    id: 'outlook',
    label: 'Outlook (Microsoft 365)',
    scopes: ['Mail.Read', 'offline_access'],
    docsUrl: 'https://learn.microsoft.com/en-us/graph/api/resources/mail-api-overview',
    authEndpoint: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    tokenEndpoint: 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
    issuer: 'login.microsoftonline.com',
  },
  imap: {
    // Manual / vault-only provider; the editor still surfaces a
    // "Configure" pill, but the dance is operator-side (paste IMAP
    // host + creds into vault, server validates).
    id: 'imap',
    label: 'IMAP',
    scopes: [],
    docsUrl: 'https://datatracker.ietf.org/doc/html/rfc3501',
    authEndpoint: '',
    tokenEndpoint: '',
    issuer: '',
  },
};

/**
 * Provider lookup that returns null for unknown ids so callers can
 * surface a friendly "unsupported provider" instead of throwing.
 */
export function getIntegrationProviderInfo(provider: string): IntegrationProviderInfo | null {
  const id = provider as IntegrationProvider;
  return INTEGRATION_PROVIDERS[id] ?? null;
}

/**
 * Default vault key for an `(org, provider)` pair, used when the
 * operator doesn't override it via the connect modal. The shape
 * mirrors the existing `gmail:{{qa_gmail_account}}` placeholder
 * that the email-OTP preset has used since Phase 2 — keeps
 * pre-cluster and post-cluster flows aligned.
 */
export function defaultVaultKeyFor(provider: IntegrationProvider, accountLabel: string): string {
  const safe = accountLabel
    .toLowerCase()
    .replace(/[^a-z0-9@._+-]/g, '_')
    .replace(/__+/g, '_')
    .slice(0, 120);
  return `${provider}:${safe}`;
}
